import { markFirstAdRequest } from '../../utils/consent/firstAdRequest'
import { render, h } from 'vue'
import { createRequestScopedComposable } from '../../utils/createRequestScopedComposable'
import Taboola from '../../components/AdOps/Taboola.vue'
import { getFallbackPlacementWidget } from '../../utils/ad-ops/adFunctions'
import loadScriptAsync from '../../utils/loadScriptAsync'
import { taboolaLoaderUrl } from '../../utils/ad-ops/adUrls'
import type { FallbackTracking } from '../../types/assertive'
import debug from '../../utils/console'
import type { NativeAdHandlers } from './useNativeAd'
const logger = debug.withTag('composables:useTaboolaAds')

declare global {
  interface Window {
    _taboola: Record<string, unknown>[]
  }
}

const console = debug.withTag('useTaboolaAds')

/**
 * Extract the brand/advertiser name from a Taboola tile element.
 */
export function getAdBrandFromTile(tileElement: Element | null): string {
  if (!tileElement) return ''

  const item =
    tileElement.querySelector('.branding-inner') ??
    tileElement.querySelector('.branding')

  if (item instanceof HTMLElement) {
    return item.innerText || item.textContent || ''
  }

  return item?.textContent || ''
}

// Taboola documents these fields but says to check each one before use, as
// they vary by product and config.
function readDetail(event: unknown) {
  const detail =
    typeof event === 'object' && event !== null
      ? Reflect.get(event, 'detail')
      : undefined
  const field = (key: string): unknown =>
    typeof detail === 'object' && detail !== null
      ? Reflect.get(detail, key)
      : undefined
  const placement = field('placement')
  const container = field('container')
  const slot = field('slot')
  return {
    placement: typeof placement === 'string' ? placement : null,
    container: container instanceof Element ? container : null,
    slot: typeof slot === 'number' ? slot : null,
  }
}

/**
 * Taboola names events after the placement we pushed, plus a ` | Card N` for
 * each card of a feed, plus an `_<item id>` on clicks.
 */
export function matchesPlacement(
  eventPlacement: string,
  placement: string
): boolean {
  const base = eventPlacement.replace(/_\d+$/, '')
  return base === placement || base.startsWith(`${placement} | `)
}

const PLACEMENT_NAME = 'data-taboola-placement'

/** The placement name an instance at `index` of a placement goes by. */
export function placementName(placement: string, index: number): string {
  return index > 0 ? `${placement}-${index}` : placement
}

/**
 * Claims the first index from `index` whose placement name no other instance
 * on the page holds, and marks `wrapper` with it. Taboola tells instances
 * apart only by placement name, and `index` can repeat: every fallback of a
 * slot starts at 0, and an empty slot above turning into a fallback shifts the
 * count down the feed.
 */
export function claimPlacementIndex(
  placement: string,
  index: number,
  wrapper: Element
): number {
  const taken = new Set(
    [...document.querySelectorAll(`[${PLACEMENT_NAME}]`)]
      .filter((element) => element !== wrapper)
      .map((element) => element.getAttribute(PLACEMENT_NAME))
  )
  let free = index
  while (taken.has(placementName(placement, free))) free++
  wrapper.setAttribute(PLACEMENT_NAME, placementName(placement, free))
  return free
}

/** The feed card a click came from. A flat feed is a single card. */
export function cardOfPlacement(eventPlacement: string): string {
  return /\| Card (\d+)/.exec(eventPlacement)?.[1] ?? '1'
}

function tileAt(wrapper: Element | null, cardIdx: string, slot: number | null) {
  if (!wrapper || slot === null) return null
  const card =
    wrapper.querySelector(`.tbl-feed-card[data-card-index="${cardIdx}"]`) ??
    wrapper
  return card.querySelectorAll('.videoCube')[slot] ?? null
}

/**
 * Registers one instance's `render` and `click` handlers. Taboola calls every
 * handler for every placement, so each only passes on events `owns` says are
 * this instance's. `notify: 'newPageLoad'` drops every handler, which is fine
 * as long as instances are recreated after one, as a category tab switch does.
 */
export function subscribeToTaboola(
  owns: (placement: string) => boolean,
  wrapper: () => Element | null,
  handlers: NativeAdHandlers
): void {
  window._taboola = window._taboola || []
  window._taboola.push({
    listenTo: 'render',
    handler: (event: unknown) => {
      const { placement, container } = readDetail(event)
      if (placement !== null && owns(placement)) {
        handlers.onRender({ element: container })
      }
    },
  })
  window._taboola.push({
    listenTo: 'click',
    handler: (event: unknown) => {
      const { placement, slot } = readDetail(event)
      if (placement === null || !owns(placement)) return
      const cardIdx = cardOfPlacement(placement)
      handlers.onClick({
        cardIdx,
        tileIdx: slot,
        tile: tileAt(wrapper(), cardIdx, slot),
      })
    },
  })
}

let fallbackCount = 0

/** A placement repeats down the feed, and Taboola container ids must be unique. */
export function fallbackContainerName(containerName: string): string {
  return `${containerName}-fallback${fallbackCount++}`
}

const useTaboolaAds = createRequestScopedComposable(() => {
  const taboolaPublisherName = useAppConfig().adops.taboola.publisherName

  const { $assertive } = useNuxtApp()
  const tagType = $assertive?.analytics?.custom?.custom_1 || ''

  const isHeadLoadedPromise =
    getPageType() === 'homepage'
      ? loadScriptAsync(
          'head',
          {
            textContent: `window._taboola = window._taboola || [];
          _taboola.push({
            homepage:'auto',
            custom_tracking:'sub_id=${tagType}'
          });
          !function (e, f, u, i) { if (!document.getElementById(i)){ e.async = 1; e.src = u; e.id = i; f.parentNode.insertBefore(e, f); } }
            (document.createElement('script'), document.getElementsByTagName('script')[0], '${taboolaLoaderUrl(taboolaPublisherName)}', 'tb_loader_script');
            if(window.performance && typeof(window.performance.mark) == 'function') {window.performance.mark('tbl_ic');}`,
            key: 'taboola-homepage-header-script',
          },
          {
            id: 'taboola-homepage-header-script',
            type: 'text/javascript',
            referrerpolicy: 'no-referrer-when-downgrade',
            async: true,
            onload: () => console.debug('Taboola script loaded.'),
            onerror: () => console.error('Taboola script failed to load.'),
          }
        )
      : loadScriptAsync(
          'head',
          {
            textContent: `window._taboola = window._taboola || [];
          _taboola.push({
            article:'auto',
            custom_tracking:'sub_id=${tagType}'
          });
          !function (e, f, u, i) { if (!document.getElementById(i)){ e.async = 1; e.src = u; e.id = i; f.parentNode.insertBefore(e, f); } }
            (document.createElement('script'), document.getElementsByTagName('script')[0], '${taboolaLoaderUrl(taboolaPublisherName)}', 'tb_loader_script');
            if(window.performance && typeof(window.performance.mark) == 'function') {window.performance.mark('tbl_ic');}`,
            key: 'taboola-article-header-script',
          },
          {
            id: 'taboola-article-header-script',
            type: 'text/javascript',
            referrerpolicy: 'no-referrer-when-downgrade',
            async: true,
            onload: () => console.debug('Taboola script loaded.'),
            onerror: () => console.error('Taboola script failed to load.'),
          }
        )

  const isBodyLoadedPromise = loadScriptAsync(
    'body',
    {
      textContent: `window._taboola = window._taboola || [];
        _taboola.push({flush: true});`,
      key: 'taboola-homepage-body-script',
    },
    {
      id: 'taboola-homepage-body-script',
      type: 'text/javascript',
      async: true,
      onload: () => console.debug('Taboola body script loaded.'),
      onerror: () => console.error('Taboola body script failed to load.'),
    }
  )

  const renderWidget = async (
    mode: string,
    taboolaContainerName: string,
    taboolaPlacementName: string
  ): Promise<void> => {
    try {
      await isHeadLoadedPromise
      await isBodyLoadedPromise
    } catch (error) {
      logger.warn(
        'Failed to load Taboola SDK, continuing without Taboola ads',
        error
      )
      return
    }

    window._taboola = window._taboola || []
    // The normal first request: the placement queued after the head and body
    // scripts have loaded. The body script's own flush runs before any
    // placement exists, so it asks for nothing on its own.
    markFirstAdRequest('taboola')
    window._taboola.push({
      mode: mode,
      container: taboolaContainerName,
      placement: taboolaPlacementName,
      target_type: 'mix',
    })
  }

  const callRenderTaboolaWidgets = async () => {
    await isHeadLoadedPromise
    await isBodyLoadedPromise
    window._taboola = window._taboola || []
    window._taboola.push({ notify: 'newPageLoad' })
    window._taboola.push({ flush: true })
  }

  // No `newPageLoad` notify — that would count an extra Taboola pageview.
  const flushTaboolaWidgets = async () => {
    await isHeadLoadedPromise
    await isBodyLoadedPromise
    window._taboola = window._taboola || []
    window._taboola.push({ flush: true })
  }

  // Renders a Taboola ad in a placement div. Used for programmatic ad fallback.
  const renderFallbackTaboolaAd = async (
    placement: string,
    element: Element | null,
    tracking?: FallbackTracking
  ): Promise<void> => {
    const widgetProps = getFallbackPlacementWidget(placement, 'taboola')

    if (!widgetProps?.containerName || !widgetProps.mode || !element) return

    const containerName = fallbackContainerName(widgetProps.containerName)
    // append fallback to the end of class to prog ads will no longer be mapped to the placement
    const newPlacementName = `${widgetProps.placement}-fallback`
    element.classList.remove(placement)
    element.classList.add(newPlacementName)

    // Erase the innerHTML of the placement div to clean scripts and other elements added by AY integration
    element.innerHTML = ''
    try {
      const { vueApp } = useNuxtApp()
      const taboolaNode = h(Taboola, {
        ...widgetProps,
        containerName,
        placement: newPlacementName,
        ...tracking,
      })
      taboolaNode.appContext = vueApp._context
      // Insert the taboola ad wrapper into the placement div
      render(taboolaNode, element)

      await nextTick()
      await flushTaboolaWidgets()
    } catch (err) {
      logger.error(
        `[Native-Fallback]: Error rendering fallback Taboola ad component ${placement}`,
        err
      )
    }
  }

  return {
    renderWidget,
    callRenderTaboolaWidgets,
    renderFallbackTaboolaAd,
  }
})

export default useTaboolaAds
